import { FiPlus } from "react-icons/fi";

import { ITrophyProgress, TrophyTier } from "MVVM/@types/trophies";
import {
  getTrophyIconPaths,
  getTrophyPalette,
  getTrophyProgressPercent,
} from "MVVM/Utilities/Trophy";
import {
  BADGE_DIMENSIONS,
  BadgeWrapper,
  CIRCUMFERENCE,
  DISK_RADIUS,
  EmptyCircle,
  RING_RADIUS,
  RING_WIDTH,
  TTrophyBadgeSize,
} from "./styledComponents";

export type { TTrophyBadgeSize };

interface ITrophyBadgeProps {
  // Omitted only for `empty` (unfilled featured slot) placeholders.
  trophyKey?: string;
  tier?: TrophyTier;
  size?: TTrophyBadgeSize;
  // Earned but not yet on this render's data (shouldn't normally happen) —
  // renders the flat locked palette instead of the tier's metal.
  locked?: boolean;
  // Unfilled featured slot — dashed "add one" circle, no ring, no glyph.
  empty?: boolean;
  // Progress toward the next tier — undefined until the backend adds the
  // underlying `value` field for this trophy; the ring just stays hidden.
  progress?: ITrophyProgress | null;
  onClick?: () => void;
  className?: string;
}

export const TrophyBadge = ({
  trophyKey,
  tier,
  size = "md",
  locked = false,
  empty = false,
  progress,
  onClick,
  className,
}: ITrophyBadgeProps) => {
  const interactive = !!onClick;

  if (empty) {
    return (
      <EmptyCircle
        $size={size}
        $interactive={interactive}
        onClick={onClick}
        className={className}
      >
        <FiPlus size={BADGE_DIMENSIONS[size] * 0.4} />
      </EmptyCircle>
    );
  }

  const palette = getTrophyPalette(tier, locked);
  const paths = getTrophyIconPaths(trophyKey ?? "");
  const percent = getTrophyProgressPercent(progress);
  const dashLength = (Math.min(percent, 100) / 100) * CIRCUMFERENCE;
  const showRing = !locked && percent > 0;
  const dimension = BADGE_DIMENSIONS[size];

  return (
    <BadgeWrapper
      $interactive={interactive}
      $opacity={locked ? 0.4 : 1}
      onClick={onClick}
      className={className}
      style={{ width: dimension, height: dimension }}
    >
      <svg
        width={dimension}
        height={dimension}
        viewBox="0 0 100 100"
        style={{ display: "block", overflow: "visible" }}
      >
        <circle
          cx="50"
          cy="50"
          r={RING_RADIUS}
          fill="none"
          stroke={palette.track}
          strokeWidth={RING_WIDTH}
        />
        <circle
          cx="50"
          cy="50"
          r={RING_RADIUS}
          fill="none"
          stroke={palette.color}
          transform="rotate(-90 50 50)"
          strokeWidth={RING_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${dashLength} ${CIRCUMFERENCE + 10}`}
          opacity={showRing ? 1 : 0}
          style={{
            filter: `drop-shadow(0 0 3px ${palette.color})`,
            transition: "stroke-dasharray .5s ease",
          }}
        />
        <circle cx="50" cy="50" r={DISK_RADIUS} fill={palette.deep} />
        <circle
          cx="50"
          cy="50"
          r={DISK_RADIUS}
          fill="none"
          stroke={palette.color}
          strokeWidth="1.5"
          strokeOpacity="0.35"
        />
        {paths.map((d) => (
          <path
            key={d}
            d={d}
            fill="none"
            stroke={palette.color}
            strokeWidth="4.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </BadgeWrapper>
  );
};
